/**
 * Revenue & Earnings screen.
 *
 * Data source: GET /admin/metrics/earnings (Phase C — start/end/granularity).
 * Adds a net-revenue timeseries chart, referred-vs-organic cohort split,
 * composition rows and the full windowed KPI grid. CSV export uses the
 * endpoint's own format=csv branch.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  compositionRows,
  downloadCsv,
  errorState,
  escape,
  fmtBps,
  fmtInt,
  fmtKobo,
  kpi,
  mountLineChart,
  rangeQuery,
  skeletonGrid,
} from './common.js';

export async function renderRevenue(host, ctx) {
  host.innerHTML = skeletonGrid(6);
  try {
    const data = await api(`/admin/metrics/earnings${rangeQuery(ctx.range)}`);
    host.innerHTML = _render(data, ctx);
    _mountCharts(host, data);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

/** Called by the shell topbar's CSV button. */
export function exportCsv({ range }) {
  const path = `/admin/metrics/earnings${rangeQuery(range).replace(/^\?/, '?format=csv&')}`;
  const withFormat = path.includes('?') ? path : `${path}?format=csv`;
  downloadCsv(withFormat, `triviana_revenue_${range.start}_${range.end}.csv`);
}

function _render(d, ctx) {
  const t = d.totals || {};
  const cohort = d.cohort || {};
  const series = d.timeseries || [];

  const heroCards = [
    kpi({
      label: 'Net revenue',
      value: fmtKobo(t.net_revenue_kobo),
      hint: 'rake + power-ups + live entry − fees',
      hero: true,
    }),
    kpi({ label: 'Take rate', value: fmtBps(t.take_rate_bps), hint: 'rake ÷ handle', hero: true }),
    kpi({ label: 'ARPPU', value: fmtKobo(t.arppu_kobo), hint: 'rev ÷ paying player', hero: true }),
    kpi({
      label: 'Paying players',
      value: fmtInt(t.paying_players),
      hint: '≥1 paid duel',
      hero: true,
    }),
  ].join('');

  const revKpis = [
    kpi({ label: 'Duel handle', value: fmtKobo(t.duel_handle_kobo), hint: 'gross player spend' }),
    kpi({ label: 'Duel rake', value: fmtKobo(t.duel_rake_kobo), hint: 'house cut' }),
    kpi({ label: 'Prizes paid', value: fmtKobo(t.duel_prizes_paid_kobo), hint: 'returned to players' }),
    kpi({
      label: 'Power-up revenue',
      value: fmtKobo(t.powerup_revenue_kobo),
      hint: 'erasers + 50/50',
    }),
    kpi({
      label: 'Live entry revenue',
      value: fmtKobo(t.live_entry_revenue_kobo),
      hint: 'paid joins − refunds',
    }),
    kpi({ label: 'Payment fees', value: fmtKobo(t.payment_fees_kobo), hint: 'processor cost' }),
    kpi({ label: 'Active players', value: fmtInt(t.active_players), hint: 'played this window' }),
    kpi({
      label: 'Paying player %',
      value: fmtBps(t.paying_player_pct_bps),
      hint: 'paying ÷ active',
    }),
    kpi({
      label: 'Outstanding eraser liab.',
      value: fmtInt(t.outstanding_eraser_balance),
      hint: 'unspent erasers owed',
    }),
    kpi({
      label: 'Outstanding 50/50 liab.',
      value: fmtInt(t.outstanding_fiftyfifty_balance),
      hint: 'unspent 50/50 owed',
    }),
  ].join('');

  const chart = `<div id="adm2-revenue-chart"></div>`;

  // Composition of the handle → prizes back + rake + powerups + fees.
  const handle = Number(t.duel_handle_kobo || 0);
  const prizes = Number(t.duel_prizes_paid_kobo || 0);
  const rake = Number(t.duel_rake_kobo || 0);
  const puRev = Number(t.powerup_revenue_kobo || 0);
  const liveEntry = Number(t.live_entry_revenue_kobo || 0);
  const fees = Number(t.payment_fees_kobo || 0);
  const composition = compositionRows([
    { label: 'Duel handle · gross spend', value: handle, display: fmtKobo(handle), pct: 100, color: '#1E3A8A' },
    { label: 'Prizes paid back to players', value: prizes, display: fmtKobo(prizes), pct: _pct(prizes, handle), color: '#9AA7BD' },
    { label: 'Duel rake · kept', value: rake, display: fmtKobo(rake), pct: _pct(rake, handle), color: '#0E7C5A' },
    { label: 'Power-up revenue', value: puRev, display: fmtKobo(puRev), pct: _pct(puRev, handle), color: '#7C53E8' },
    { label: 'Live entry revenue', value: liveEntry, display: fmtKobo(liveEntry), pct: _pct(liveEntry, handle), color: '#E5A630' },
    { label: 'Payment fees', value: fees, display: fmtKobo(fees), pct: _pct(fees, handle), color: '#D92D20' },
    {
      label: 'Net revenue',
      value: t.net_revenue_kobo || 0,
      display: fmtKobo(t.net_revenue_kobo),
      pct: _pct(t.net_revenue_kobo, handle),
      color: '#16264F',
    },
  ]);

  const cohortRows = `
    <div style="display:grid;grid-template-columns:1.2fr 1fr 1fr 1fr;gap:8px;font:600 10px 'IBM Plex Sans',sans-serif;letter-spacing:.04em;text-transform:uppercase;color:var(--a-faint);padding-bottom:8px;border-bottom:1px solid var(--a-border);">
      <span>Segment</span><span style="text-align:right;">Users</span>
      <span style="text-align:right;">Revenue</span><span style="text-align:right;">Avg</span>
    </div>
    ${_cohortRow('Referred', cohort.referred_users, cohort.referred_revenue_kobo, cohort.referred_avg_revenue_kobo, '#7C53E8')}
    ${_cohortRow('Organic', cohort.organic_users, cohort.organic_revenue_kobo, cohort.organic_avg_revenue_kobo, '#1E3A8A')}
    ${_cohortRow(
      'All players',
      (cohort.referred_users || 0) + (cohort.organic_users || 0),
      (cohort.referred_revenue_kobo || 0) + (cohort.organic_revenue_kobo || 0),
      Math.round(
        ((cohort.referred_revenue_kobo || 0) + (cohort.organic_revenue_kobo || 0)) /
          Math.max(1, (cohort.referred_users || 0) + (cohort.organic_users || 0)),
      ),
      '#9AA7BD',
    )}
  `;

  return `
    <div class="adm2-section-label">Hero metrics · this period</div>
    <div class="adm2-grid hero">${heroCards}</div>

    <div class="adm2-panel" style="margin-top:24px;">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;">
        <div>
          <div class="adm2-panel-title">Net revenue trend</div>
          <div class="adm2-panel-sub">daily/weekly buckets · rake + eraser revenue − payment fees</div>
        </div>
      </div>
      <div style="margin-top:16px;">${chart}</div>
    </div>

    <div class="adm2-two">
      <div class="adm2-panel">
        <div class="adm2-panel-title">Revenue composition</div>
        <div class="adm2-panel-sub">handle → prizes back + rake; + power-ups − fees = net</div>
        <div style="margin-top:16px;">${composition}</div>
      </div>
      <div class="adm2-panel">
        <div class="adm2-panel-title">Cohort split · referred vs organic</div>
        <div class="adm2-panel-sub">users · revenue · average</div>
        <div style="margin-top:16px;">${cohortRows}</div>
      </div>
    </div>

    <div class="adm2-section-label">All revenue metrics · windowed</div>
    <div class="adm2-grid">${revKpis}</div>
  `;
}

function _mountCharts(host, d) {
  const chartHost = host.querySelector('#adm2-revenue-chart');
  const series = d.timeseries || [];
  // Note: live-entry revenue isn't in the daily timeseries yet (no
  // catalogued daily aggregate), so the chart is a per-day proxy —
  // the windowed total on the KPI card is authoritative.
  const netSeries = series.map(
    (r) =>
      Number(r.duel_rake_kobo || 0) +
      Number(r.eraser_revenue_kobo || 0) +
      Number(r.fiftyfifty_revenue_kobo || 0) -
      Number(r.payment_fees_kobo || 0),
  );
  mountLineChart(chartHost, {
    series: [
      { name: 'Net revenue', color: '#8B6BF2', values: netSeries, valueFmt: (v) => fmtKobo(v) },
    ],
    xLabels: series.map((r) => r.date),
    height: 240,
    emptyMessage: 'No trend data yet.',
  });
}

function _cohortRow(seg, users, revenue, avg, color) {
  return `
    <div style="display:grid;grid-template-columns:1.2fr 1fr 1fr 1fr;gap:8px;align-items:center;padding:11px 0;border-bottom:1px solid var(--a-border);">
      <span style="display:inline-flex;align-items:center;gap:8px;font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">
        <span style="width:8px;height:8px;border-radius:2px;background:${escape(color)};"></span>${escape(seg)}
      </span>
      <span style="text-align:right;font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">${escape(fmtInt(users))}</span>
      <span style="text-align:right;font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">${escape(fmtKobo(revenue))}</span>
      <span style="text-align:right;font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">${escape(fmtKobo(avg))}</span>
    </div>
  `;
}

function _pct(numer, denom) {
  const n = Number(numer || 0);
  const d = Number(denom || 0);
  if (d <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((100 * n) / d)));
}

/** Sample up to 7 evenly spaced tick labels from a dense array of dates. */
function _sampleTicks(dates) {
  if (!dates || !dates.length) return [];
  const n = dates.length;
  if (n <= 7) return dates.slice();
  const step = (n - 1) / 6;
  const out = [];
  for (let i = 0; i < 7; i += 1) {
    out.push(dates[Math.round(i * step)]);
  }
  return out;
}
